import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';

const ToastContext = createContext(null);

const TOAST_TYPES = {
  success: { icon: '✓' },
  error: { icon: '✕' },
  warning: { icon: '⚠' },
  info: { icon: 'ℹ' },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef(new Map());

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((tid) => clearTimeout(tid));
      timeoutsRef.current.clear();
    };
  }, []);

  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    
    setToasts(prev => [...prev, { id, message, type, duration }]);
    
    const tid = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      timeoutsRef.current.delete(id);
    }, duration);
    timeoutsRef.current.set(id, tid);
    
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    const tid = timeoutsRef.current.get(id);
    if (tid) {
      clearTimeout(tid);
      timeoutsRef.current.delete(id);
    }
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Удобные методы
  const showSuccess = useCallback((message) => addToast(message, 'success'), [addToast]);
  const showError = useCallback((message) => addToast(message, 'error', 5000), [addToast]);
  const showWarning = useCallback((message) => addToast(message, 'warning', 4000), [addToast]);
  const showInfo = useCallback((message) => addToast(message, 'info'), [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, showSuccess, showError, showWarning, showInfo }}>
      {children}
      
      {/* Контейнер для тостов */}
      <div style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        zIndex: 'var(--z-toast)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        pointerEvents: 'none'
      }}>
        {toasts.map((toast) => {
          const typeConfig = TOAST_TYPES[toast.type] || TOAST_TYPES.info;
          
          return (
            <div
              key={toast.id}
              className={`toast-item toast-item--${toast.type}`}
              style={{
                padding: '14px 20px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: '280px',
                maxWidth: '400px',
                animation: 'slideInRight 0.3s ease-out',
                pointerEvents: 'auto',
                cursor: 'pointer',
              }}
              onClick={() => removeToast(toast.id)}
            >
              <span className="toast-item__icon">
                {typeConfig.icon}
              </span>
              <span style={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>
                {toast.message}
              </span>
              <button
                type="button"
                className="toast-item__close"
                onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      
      {/* Стили анимации */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

